/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Hyunsu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Thu Nov 7 2023
 **/

import React, { Fragment, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
	Button,
	Column,
	Divider,
	Loader,
	Row,
	Select,
	Tooltip,
	Switch,
	Typography,
	TypographyTooltip
} from "@/frameworks/designStuff/components";
import { _T } from "@rfice/language";
import { Maybe } from "@/frameworks/libraries/components";

import Layout from "../../Layout";
import * as S from "../../emotion";
import { useUsers } from "@/frameworks/core/API";
import { useChatRoomActions } from "@/frameworks/core/chat";
import { OptionTime } from "./types";
import { calculateTimeOption, convertTime, defaultTime } from "./utils";
import { colors } from "@/styles";

const dayNameList = _T("월 화 수 목 금 토 일").split(" ");
const dayOfWeekList = {
	MONDAY: dayNameList[0],
	TUESDAY: dayNameList[1],
	WEDNESDAY: dayNameList[2],
	THURSDAY: dayNameList[3],
	FRIDAY: dayNameList[4],
	SATURDAY: dayNameList[5],
	SUNDAY: dayNameList[6]
};

export default function Alarm() {
	const chatRoomActions = useChatRoomActions();
	const userAPI = useUsers({
		onSuccess(name) {
			switch (name) {
				case "notification:inquiry": {
					setIsInit(false);
					break;
				}
				default:
					return;
			}
		}
	});

	const {
		isFetching,
		data: state,
		refetch
	} = useQuery({
		queryKey: ["notification:inquiry"],
		refetchOnWindowFocus: false,
		refetchOnMount: true,
		queryFn: async () => {
			return (await userAPI.notification.inquiry.execute()).data;
		}
	});

	const [isInit, setIsInit] = useState(true);
	const [ignoreWeeks, setIgnoreWeeks] = useState<string[]>([
		"MONDAY",
		"TUESDAY",
		"WEDNESDAY",
		"THURSDAY",
		"FRIDAY",
		"SATURDAY",
		"SUNDAY"
	]);
	useEffect(() => {
		if (state?.ignoreWeeks) setIgnoreWeeks(state?.ignoreWeeks);
		else setIgnoreWeeks(["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"]);
	}, [state?.ignoreWeeks]);

	const queryClient = useQueryClient();
	const [startTimeOption, setStartTimeOption] = useState<OptionTime[]>(
		calculateTimeOption(convertTime(defaultTime.startTimeValue), false)
	);
	const [endTimeOption, setEndTimeOption] = useState<OptionTime[]>(
		calculateTimeOption(convertTime(state?.ignoreStartTime || defaultTime.startTimeValue), true)
	);

	useEffect(() => {
		const startTime = convertTime(state?.ignoreStartTime || defaultTime.startTimeValue);
		setEndTimeOption(calculateTimeOption(startTime, true));
	}, [state?.ignoreStartTime]);

	useEffect(() => {
		setStartTimeOption(calculateTimeOption(convertTime(defaultTime.startTimeValue), false));
	}, [state?.ignoreEndTime]);

	// 저장하기 버튼이 삭제되고, 클릭 후, 자동으로 저장하기 기획요구
	useEffect(() => {
		if (isInit) return;
		handleSave();
	}, [state, isInit]);

	const handleChange = (
		e: React.ChangeEvent<HTMLElement>,
		type: keyof WebServices.UserOption.GetNotificationOption.Data
	) => {
		queryClient.setQueryData<WebServices.UserOption.GetNotificationOption.Data>(
			["notification:inquiry"],
			(prev) => {
				if (!prev) return prev;
				switch (type) {
					case "useChatNotification": {
						return {
							...prev,
							[type]: !prev[type],
							useDmNotification: !prev[type],
							useDmPushNotification: !prev[type]
						};
					}
					case "ignoreNotification":
						if (!prev[type]) {
							return {
								...prev,
								[type]: !prev[type],
								ignoreStartTime: state?.ignoreStartTime ? state?.ignoreStartTime : "18:00:00",
								ignoreEndTime: state?.ignoreEndTime ? state?.ignoreEndTime : "09:00:00"
							};
						} else {
							return {
								...prev,
								[type]: !prev[type],
								ignoreStartTime: state?.ignoreStartTime,
								ignoreEndTime: state?.ignoreEndTime
							};
						}

					case "mobileNotificationType": {
						return {
							...prev,
							[type]: (e.target as HTMLInputElement)
								.value as WebServices.UserOption.GetNotificationOption.Data["mobileNotificationType"]
						};
					}

					case "ignoreStartTime": {
						return {
							...prev,
							[type]: (e.target as HTMLInputElement)
								.value as WebServices.UserOption.GetNotificationOption.Data["ignoreStartTime"]
						};
					}

					case "ignoreEndTime": {
						return {
							...prev,
							[type]: (e.target as HTMLInputElement)
								.value as WebServices.UserOption.GetNotificationOption.Data["ignoreEndTime"]
						};
					}

					default:
						return {
							...prev,
							[type]: !prev[type]
						};
				}
			}
		);
	};

	useEffect(() => {
		if (state && state?.ignoreNotification && !state.ignoreWeeks) {
			userAPI.notification.update.execute({
				...state,
				ignoreWeeks: []
			} as WebServices.UserOption.UpdateNotificationOption.Request);
		}
	}, [state]);

	const handleDayOfWeek = (dayType: keyof typeof dayOfWeekList) => {
		if (!state) return;
		setIgnoreWeeks((prevIgnoreWeeks) => {
			const dayTypeExistIndex = prevIgnoreWeeks.find((d) => d === dayType);
			let updatedIgnoreWeeks;
			if (!dayTypeExistIndex) {
				updatedIgnoreWeeks = prevIgnoreWeeks.concat(dayType);
			} else {
				updatedIgnoreWeeks = prevIgnoreWeeks.filter((item) => item !== dayType);
			}
			userAPI.notification.update.execute({
				...state,
				ignoreWeeks: updatedIgnoreWeeks
			} as WebServices.UserOption.UpdateNotificationOption.Request);
			return updatedIgnoreWeeks;
		});
	};
	const handleSave = () => {
		userAPI.notification.update.execute({
			useSpaceNotification: state?.useSpaceNotification,
			useChatNotification: state?.useChatNotification,
			useDmNotification: state?.useDmNotification,
			useDmPushNotification: state?.useDmPushNotification,
			useDmSoundNotification: state?.useDmSoundNotification,
			useSpaceChatNotification: state?.useSpaceChatNotification,
			useSpaceChatPushNotification: state?.useSpaceChatPushNotification,
			useSpaceChatSoundNotification: state?.useSpaceChatSoundNotification,
			ignoreNotification: state?.ignoreNotification,
			ignoreWeeks: state?.ignoreNotification ? ignoreWeeks : [],
			ignoreStartTime: state?.ignoreStartTime,
			ignoreEndTime: state?.ignoreEndTime,
			mobileNotificationType: state?.mobileNotificationType,
			useMeetingNotification: state?.useMeetingNotification,
			useHangupNoResponseCallMail: state?.useHangupNoResponseCallMail
		} as WebServices.UserOption.UpdateNotificationOption.Request);
	};

	if (isFetching) {
		return <Loader />;
	}

	return (
		<Layout title="알림 설정" onClose={() => refetch()}>
			<S.AlarmContent css={{ padding: "0 8px", marginBottom: "12px" }}>
				<S.AlarmSection>
					<Row gap={2}>
						<Typography.p typoSize="Title1">{_T("• 스페이스 알림")}</Typography.p>
						<Tooltip
							message={_T("스페이스에 관한 모든 알림 (입장, 초대, 요청) 등 설정할 수 있어요")}
							position="top"
							hasIcon={false}
						>
							<Button.Icon name="More24" size="small_18" filled="transparent" />
						</Tooltip>
					</Row>
					<Switch
						checked={state?.useSpaceNotification}
						onChange={(e) => handleChange(e, "useSpaceNotification")}
					/>
				</S.AlarmSection>
				<S.AlarmSection>
					<Row gap={2}>
						<Typography.p typoSize="Title1">{_T("• 채팅 알림")}</Typography.p>
						<Tooltip
							message={_T("다이렉트 메시지 알림과 채널 별 알림을 설정할 수 있어요")}
							position="top"
							hasIcon={false}
						>
							<Button.Icon name="More24" size="small_18" filled="transparent" />
						</Tooltip>
					</Row>
					<Switch
						checked={state?.useChatNotification}
						onChange={(e) => handleChange(e, "useChatNotification")}
					/>
				</S.AlarmSection>
			</S.AlarmContent>

			<S.AlarmContent css={{ padding: "0 40px" }}>
				<Maybe test={state?.useChatNotification}>
					<Column css={{ width: "100%", marginBottom: "8px" }} horizonAlign="left" gap={4}>
						<Typography.p typoSize="Title2">{_T("모바일 기기로 알림 받기")}</Typography.p>
						<Select
							disabled={!state?.useChatNotification}
							value={state?.mobileNotificationType}
							onChange={(e) => handleChange(e, "mobileNotificationType")}
							options={[
								{
									label: _T("데스크탑 활성 상태일 때도 항상"),
									value: "WEB_ACTIVE"
								},
								{
									label: _T("데스크탑 비활성화 상태가 되자마자"),
									value: "WEB_INACTIVE"
								}
							]}
						></Select>
					</Column>

					<S.AlarmSection>
						<Typography.p typoSize="Title2">{_T("다이렉트 채팅 알림 관리")}</Typography.p>
						<Switch
							checked={state?.useDmNotification}
							onChange={(e) => handleChange(e, "useDmNotification")}
						/>
					</S.AlarmSection>

					<S.AlarmSubContent>
						<S.AlarmSection css={{ paddingBottom: "12px" }}>
							<Typography.p typoSize="Body3">{_T("PUSH 알림")}</Typography.p>
							<Switch
								disabled={!state?.useDmNotification}
								checked={state?.useDmPushNotification}
								onChange={(e) => handleChange(e, "useDmPushNotification")}
							/>
						</S.AlarmSection>
						<Column horizonAlign="right" css={{ width: "100%" }}>
							<S.AlarmSection>
								<Typography.p typoSize="Body3">{_T("소리 알림")}</Typography.p>
								<Switch
									disabled={!state?.useDmNotification}
									checked={Boolean(state?.useDmSoundNotification)}
									onChange={(e) => handleChange(e, "useDmSoundNotification")}
								/>
							</S.AlarmSection>
						</Column>
					</S.AlarmSubContent>

					<S.AlarmSection>
						<Typography.p typoSize="Title2">{_T("전체 스페이스 채팅 알림 관리")}</Typography.p>
						<Switch
							checked={state?.useSpaceChatNotification}
							onChange={(e) => handleChange(e, "useSpaceChatNotification")}
						/>
					</S.AlarmSection>

					<S.AlarmSubContent>
						<S.AlarmSection>
							<Typography.p typoSize="Body3">{_T("PUSH 알림")}</Typography.p>
							<Switch
								checked={state?.useSpaceChatPushNotification}
								disabled={!state?.useSpaceChatNotification}
								onChange={(e) => handleChange(e, "useSpaceChatPushNotification")}
							/>
						</S.AlarmSection>

						<Column horizonAlign="right" css={{ width: "100%", marginBottom: "12px" }}>
							<S.AlarmSection>
								<Typography.p typoSize="Body3">{_T("소리 알림")}</Typography.p>
								<Switch
									checked={state?.useSpaceChatSoundNotification}
									disabled={!state?.useSpaceChatNotification}
									onChange={(e) => handleChange(e, "useSpaceChatSoundNotification")}
								/>
							</S.AlarmSection>
						</Column>

						<Divider type="line" css={{ padding: "8px 0" }} />

						<S.AlarmSection>
							<S.AlarmSection>
								<Typography.p typoSize="Body3">{_T("마이룸")}</Typography.p>
							</S.AlarmSection>
							<S.AlarmSelect>
								<Select
									onChange={(e) => {
										chatRoomActions.common.updateNotification({
											roomID: String(state?.spaceChatNotification.myRoomChat?.roomId),
											notificationType: e.target.value as AlarmOptions
										});
									}}
									value={_T("알람 켜기")}
									disabled={!state?.useSpaceChatNotification}
									options={[
										{
											label: _T("알람 켜기"),
											value: _T("알람 켜기")
										},
										{
											label: _T("알람 끄기"),
											value: _T("알람 끄기")
										}
									]}
								/>
							</S.AlarmSelect>
						</S.AlarmSection>

						<Maybe test={state && state?.spaceChatNotification.spaces.length > 0}>
							{state?.spaceChatNotification.spaces.map((space) => {
								return (
									<Fragment key={space.groupId}>
										<Divider type="line" css={{ margin: "8px 0" }} />
										<S.AlarmSection>
											<TypographyTooltip.p
												tooltipProps={{ hasIcon: false }}
												componentProps={{
													maxLine: 1,
													align: "left",
													ellipsis: true,
													stretch: true,
													typoSize: "Body3"
												}}
											>
												{_T("{{groupName}} ・ 라운지", { groupName: space.groupName })}
											</TypographyTooltip.p>
											<S.AlarmSelect>
												<SelectNotificationType
													disabled={!state?.useSpaceChatNotification}
													value={space.notificationType}
													onChange={(e) => {
														chatRoomActions.common.updateNotification({
															roomID: space.roomId,
															notificationType: e.target.value as AlarmOptions
														});
													}}
												/>
											</S.AlarmSelect>
										</S.AlarmSection>

										<Divider type="line" css={{ margin: "8px 0" }} />

										{space.spaceChats.map((spaceChat) => (
											<Fragment key={spaceChat.spaceName}>
												<S.AlarmContainer>
													<TypographyTooltip.p
														tooltipProps={{ hasIcon: false }}
														componentProps={{
															maxLine: 1,
															align: "left",
															ellipsis: true,
															style: { margin: "10px 0" },
															stretch: true,
															typoSize: "Body3"
														}}
													>
														{spaceChat.spaceName}
													</TypographyTooltip.p>
												</S.AlarmContainer>
												<S.ChannelWrapperList>
													{spaceChat.rooms.map((room) => (
														<S.ChannelItem key={room.roomId}>
															<S.ChannelItemContainer gap={"16px"}>
																<S.AlarmSection
																	style={{ maxWidth: "calc(100% - 216px)" }}
																>
																	<Typography.p maxLine={1} ellipsis typoSize="Body4">
																		{room.roomName || ""}
																	</Typography.p>
																</S.AlarmSection>
																<S.AlarmSelect>
																	<SelectNotificationType
																		value={room.notificationType}
																		onChange={(e) => {
																			chatRoomActions.common.updateNotification({
																				roomID: room.roomId,
																				notificationType: e.target
																					.value as AlarmOptions
																			});
																		}}
																		disabled={!state?.useSpaceChatNotification}
																	/>
																</S.AlarmSelect>
															</S.ChannelItemContainer>
														</S.ChannelItem>
													))}
												</S.ChannelWrapperList>
											</Fragment>
										))}
									</Fragment>
								);
							})}
						</Maybe>
					</S.AlarmSubContent>
				</Maybe>
			</S.AlarmContent>

			<S.AlarmContent css={{ padding: "0 8px" }}>
				<S.AlarmSection>
					<Row gap={2}>
						<Typography.p typoSize="Title1">{_T("• 미팅 알림")}</Typography.p>
						<Tooltip
							message={_T("미팅에 관한 모든 알림 (초대, 예약, 스케쥴) 등 설정할 수 있어요")}
							position="top"
							hasIcon={false}
						>
							<Button.Icon name="More24" size="small_18" filled="transparent" />
						</Tooltip>
					</Row>
					<Switch
						checked={state?.useMeetingNotification}
						onChange={(e) => handleChange(e, "useMeetingNotification")}
					/>
				</S.AlarmSection>
			</S.AlarmContent>
			<S.AlarmContent>
				<S.AlarmSection css={{ paddingRight: "8px", paddingLeft: "8px" }}>
					<Typography.p typoSize="Title1">{_T("• 알림 금지 시간 설정")}</Typography.p>
					<Switch
						checked={state?.ignoreNotification}
						onChange={(e) => handleChange(e, "ignoreNotification")}
					/>
				</S.AlarmSection>
				<Column css={{ padding: "0 24px", width: "100%" }}>
					<Typography.p typoSize="Body3" typoColor="neutral_60_100" css={{ marginBottom: "18px" }}>
						{_T("선택한 시간동안 알림이 울리지 않습니다.")}
					</Typography.p>
					<S.ContentBox
						gap={20}
						css={{
							marginBottom: "20px",
							pointerEvents: state?.ignoreNotification ? "all" : "none",
							opacity: state?.ignoreNotification ? 1 : 0.5
						}}
					>
						<Column gap={4} css={{ width: "100%" }}>
							<Typography.p typoSize="Body4" typoColor="neutral_60_100">
								{_T("날짜 선택")}
							</Typography.p>
							<Button.Group css={{ gap: "8px", width: "100%" }}>
								{Object.keys(dayOfWeekList)
									.map((key) => ({
										key,
										value: dayOfWeekList[key as keyof typeof dayOfWeekList]
									}))
									.map((item) => (
										<Button
											key={item.key}
											onClick={() => handleDayOfWeek(item.key as keyof typeof dayOfWeekList)}
											size="small"
											shape="circle"
											css={{
												width: "100%",
												maxWidth: "60px",
												backgroundColor:
													ignoreWeeks?.indexOf(item.key as keyof typeof dayOfWeekList) !== -1
														? colors.secondary_80_8
														: colors.neutral_80_100
											}}
											typoSize="Body3_MD"
											typoColor={
												ignoreWeeks?.indexOf(item.key as keyof typeof dayOfWeekList) !== -1
													? "neutral_40_100"
													: "neutral_00_100"
											}
										>
											{item.value}
										</Button>
									))}
							</Button.Group>
						</Column>
						<Column gap={4} css={{ width: "100%" }}>
							<Typography.p typoSize="Body4" typoColor="neutral_60_100">
								{_T("시간 선택")}
							</Typography.p>

							<Row verticalAlign="center" gap={4} css={{ width: "100%" }}>
								<Select
									value={state?.ignoreStartTime}
									options={startTimeOption}
									onChange={(e) => handleChange(e, "ignoreStartTime")}
									disabled={ignoreWeeks.length === 7}
								/>
								<span>~</span>
								<Select
									value={state?.ignoreEndTime}
									options={endTimeOption}
									onChange={(e) => handleChange(e, "ignoreEndTime")}
									disabled={ignoreWeeks.length === 7}
								/>
							</Row>
						</Column>
					</S.ContentBox>
				</Column>
			</S.AlarmContent>
			<S.AlarmContent css={{ padding: "0 8px" }}>
				<S.AlarmSection>
					<Row gap={2}>
						<Typography.p typoSize="Title1">{_T("• 부재중 전화 메일 알림 설정")}</Typography.p>
						<Tooltip
							message={_T("부재중 전화 생성 시 계정 메일로 알림 메일이 발송됩니다.")}
							position="top"
							hasIcon={false}
						>
							<Button.Icon name="More24" size="small_18" filled="transparent" />
						</Tooltip>
					</Row>
					<Switch
						checked={state?.useHangupNoResponseCallMail}
						onChange={(e) => handleChange(e, "useHangupNoResponseCallMail")}
					/>
				</S.AlarmSection>
			</S.AlarmContent>
		</Layout>
	);
}

function SelectNotificationType(props: {
	value?: string;
	disabled: boolean;
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
	return (
		<Select
			value={props.value}
			disabled={props.disabled}
			onChange={props.onChange}
			options={[
				{
					label: _T("모든 메시지"),
					value: "ALL"
				},
				{
					label: _T("@멘션만"),
					value: "ONLY_MENTION"
				},
				{
					label: _T("꺼짐"),
					value: "OFF"
				}
			]}
		/>
	);
}
